import { makeAutoObservable } from 'mobx'

export type ICustomizeBlockState = {
  color: string
  position: string
  header: string
  message: string
  opt_in_text: string
  button: string
  themeCount: number
}

const DEFAULT_OPT_IN_TEXT =
  'By checking this box, I agree to receive recurring automated [YOUR USE CASE] text messages from {org_name} regarding [DESCRIBE TYPES OF MESSAGES YOU WANT TO SEND RELATED TO YOUR USE CASE]. Message frequency may vary. Msg & data rates may apply. Reply STOP to cancel or HELP for assistance. By checking, I accept Terms of Service and Privacy Policy.'

const ORG_NAME_TOKEN_PLACEHOLDER = /\{org_name\}/g

export const applyOrgNamePlaceholder = (text: string, orgName: string | null | undefined): string =>
  orgName ? text.replace(ORG_NAME_TOKEN_PLACEHOLDER, orgName) : text

const getDefaultOptInText = (orgName: string | null): string =>
  applyOrgNamePlaceholder(DEFAULT_OPT_IN_TEXT, orgName)

const DEFAULT_CUSTOMIZE = (orgName: string | null): Omit<ICustomizeBlockState, 'themeCount'> => ({
  color: '#068FF9',
  position: 'right',
  header: 'How can we help?',
  message: 'Hi there, have a question? Text us here',
  opt_in_text: getDefaultOptInText(orgName),
  button: 'Send',
})

const HEADER_ERROR = 'Enter header text'
const MESSAGE_ERROR = 'Enter welcome message'
const COMPLIANCE_ERROR = 'Enter compliance text'
const BUTTON_ERROR = 'Enter button text'
const ASSIGN_INBOX_ERROR = 'Select inbox'
const HEADER_MAX_LENGTH = 40
const MESSAGE_MAX_LENGTH = 255
const COMPLIANCE_MAX_LENGTH = 500
const BUTTON_MAX_LENGTH = 30

export class CustomizeBlockStore {
  color: string
  position: string
  header: string
  message: string
  opt_in_text: string
  button: string
  headerError: string | null = null
  messageError: string | null = null
  complianceError: string | null = null
  buttonError: string | null = null
  assignInboxError: string | null = null

  constructor(orgName: string | null = null) {
    const defaults = DEFAULT_CUSTOMIZE(orgName)
    this.color = defaults.color
    this.position = defaults.position
    this.header = defaults.header
    this.message = defaults.message
    this.opt_in_text = defaults.opt_in_text
    this.button = defaults.button
    makeAutoObservable(this)
  }

  clearErrors = () => {
    this.headerError = null
    this.messageError = null
    this.complianceError = null
    this.buttonError = null
    this.assignInboxError = null
  }

  clearAssignInboxError = () => {
    this.assignInboxError = null
  }

  validate(teamId?: number | null): boolean {
    this.headerError = this.header.trim() === '' ? HEADER_ERROR : null
    this.messageError = this.message.trim() === '' ? MESSAGE_ERROR : null
    this.complianceError = this.opt_in_text.trim() === '' ? COMPLIANCE_ERROR : null
    this.buttonError = this.button.trim() === '' ? BUTTON_ERROR : null
    this.assignInboxError = teamId === undefined || teamId === null ? ASSIGN_INBOX_ERROR : null
    return (
      this.headerError === null &&
      this.messageError === null &&
      this.complianceError === null &&
      this.buttonError === null &&
      this.assignInboxError === null
    )
  }

  syncFromApi(settings: {
    color: string
    position: string
    header: string
    message: string
    button: string
    opt_in_text: string
  }) {
    this.color = settings.color
    this.position = settings.position
    this.header = settings.header
    this.message = settings.message
    this.button = settings.button
    this.opt_in_text = settings.opt_in_text
  }

  setColor = (value: string) => {
    this.color = value
  }

  setPosition = (value: string) => {
    this.position = value
  }

  setHeader = (value: string) => {
    this.header = value.slice(0, HEADER_MAX_LENGTH)
    if (this.headerError !== null)
      this.headerError = this.header.trim() === '' ? HEADER_ERROR : null
  }

  setMessage = (value: string) => {
    this.message = value.slice(0, MESSAGE_MAX_LENGTH)
    if (this.messageError !== null)
      this.messageError = this.message.trim() === '' ? MESSAGE_ERROR : null
  }

  setOptInText = (value: string) => {
    this.opt_in_text = value.slice(0, COMPLIANCE_MAX_LENGTH)
    if (this.complianceError !== null)
      this.complianceError = this.opt_in_text.trim() === '' ? COMPLIANCE_ERROR : null
  }

  setButton = (value: string) => {
    this.button = value.slice(0, BUTTON_MAX_LENGTH)
    if (this.buttonError !== null)
      this.buttonError = this.button.trim() === '' ? BUTTON_ERROR : null
  }

  getSnapshot(): Pick<
    ICustomizeBlockState,
    'color' | 'position' | 'header' | 'message' | 'button' | 'opt_in_text'
  > {
    return {
      color: this.color,
      position: this.position,
      header: this.header,
      message: this.message,
      button: this.button,
      opt_in_text: this.opt_in_text,
    }
  }
}
